import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import styled from 'styled-components'

import { isProBrand as isProBrandSelector } from 'store/currentUser/selectors'

import { SUBMISSIONS_CONTACT } from 'routes/paths'

import PeopleEmpty from './PeopleEmpty'
import { PeopleCard } from 'features/LeadGen/RetailerProfile/components/PeopleCard/PeopleCard'
import { ViewModeIds } from 'features/LeadGen/RetailerProfile/retailerProfilePage.ids'
import type { RetailerProfile } from 'features/LeadGen/RetailerProfile/retailerProfilePage.types'

export const USER_SETTINGS_URL = '/accounts/settings'

const SectionContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  margin-top: 24px;
`

export type PeopleProps = {
  people: RetailerProfile['people']
  isRetailer: boolean
  isRetailerConnected?: boolean
  retailerId?: string
  accountSubmission?: RetailerProfile['accountSubmission']
}

const People = ({
  people,
  isRetailer,
  isRetailerConnected,
  retailerId,
  accountSubmission,
}: PeopleProps) => {
  const { brandSubmissionsFlag } = useFlags()

  const isEmpty = !people || people?.length === 0

  const isProBrand = useSelector(isProBrandSelector)

  const canSendSubmission =
    isProBrand && !isRetailerConnected && brandSubmissionsFlag

  const sentAccountSubmission = accountSubmission?.messageId
    ? {
        rep:
          accountSubmission.senderAccount?.accountUserName ||
          accountSubmission.senderAccount?.accountUserName ||
          '',
        date: accountSubmission.createdAt,
        messageId: accountSubmission.messageId,
      }
    : undefined

  return (
    <SectionContainer
      id={ViewModeIds.PeopleWidget.Container}
      data-testid="people-section"
    >
      {isEmpty ? (
        <PeopleEmpty
          isRetailer={isRetailer}
          contactUrl={
            canSendSubmission && retailerId
              ? generatePath(SUBMISSIONS_CONTACT, {
                  retailerId: retailerId,
                  accountUserId: '',
                })
              : undefined
          }
          submissionSent={sentAccountSubmission}
        />
      ) : (
        people.map(
          (
            { id, name, jobTitle, phone, profilePictureUrl, submissions },
            index,
          ) => {
            const submission = submissions?.[0]

            const contactUrl =
              canSendSubmission &&
              generatePath(SUBMISSIONS_CONTACT, {
                retailerId: retailerId!,
                accountUserId: id || '',
              })

            const submissionSent = submission?.messageId
              ? {
                  rep:
                    submission.senderAccount?.accountUserName ||
                    submission.senderAccount?.accountName ||
                    '',
                  date: submission.createdAt,
                  messageId: submission.messageId,
                }
              : undefined

            return (
              <PeopleCard
                key={index}
                name={name || ''}
                jobTitle={jobTitle}
                phone={phone}
                profileImageUrl={profilePictureUrl}
                contactUrl={contactUrl}
                submissionSent={submissionSent}
                isRetailer={isRetailer}
              />
            )
          },
        )
      )}
    </SectionContainer>
  )
}

export default People
